import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { NormalizedOrderListItem, normalizeOrdersForList } from "../helpers/order";
import { getPrimaryShopId } from "../helpers/shop";

export interface UseShopOrdersOptions {
    formatter?: (response: any) => NormalizedOrderListItem[];
}

type Fetcher = (shopId: number) => Promise<any>;

export const useShopOrders = (fetcher: Fetcher, options?: UseShopOrdersOptions) => {
    const authUser = useSelector((state: any) => state.auth?.user);
    const shopId = useMemo(() => getPrimaryShopId(authUser), [authUser]);
    const [orders, setOrders] = useState<NormalizedOrderListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const formatter = options?.formatter ?? normalizeOrdersForList;

    const loadOrders = useCallback(async () => {
        if (!shopId) {
            setOrders([]);
            setError("Không tìm thấy cửa hàng của bạn. Vui lòng liên hệ quản trị viên.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetcher(shopId);
            setOrders(formatter(response));
        } catch (err: any) {
            const message = typeof err === "string" ? err : "Không thể tải dữ liệu đơn hàng.";
            setError(message);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [fetcher, formatter, shopId]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    return {
        orders,
        loading,
        error,
        reload: loadOrders,
        hasShop: Boolean(shopId),
    };
};
